<template>
  <div class="wc-atc-button-modern">
    <WCAddToListModernCardButton
      v-if="listOnly && !isMto"
      class="wc-atc-button-modern__action"
      :item="item"
      :btnVariant="addToListInferredVariant"
    />

    <button
      v-else-if="notAvailable"
      :id="`atc-not-available-${item.id}`"
      type="button"
      class="wc-atc-button-modern__pill"
      disabled
    >
      {{ t('notAvailable') }}
    </button>

    <button
      v-else-if="outOfStock"
      :id="`atc-out-of-stock-${item.id}`"
      type="button"
      class="wc-atc-button-modern__pill"
      disabled
    >
      <font-awesome-icon :icon="['fas', 'ban']" aria-hidden="true" />
      {{ t('outOfStock') }}
    </button>

    <button
      v-else-if="inStoreOnly"
      :id="`atc-in-store-only-${item.id}`"
      type="button"
      class="wc-atc-button-modern__pill"
      disabled
    >
      {{ t('inStoreOnly') }}
    </button>

    <div
      v-else-if="cardState === 'inOtherCart' || cardState === 'inOtherCarts'"
      class="wc-atc-button-modern__multi-ot wc-atc-button-modern__multi-ot--label"
    >
      <span class="wc-atc-button-modern__multi-ot-label">
        <font-awesome-icon :icon="['fas', 'check']" aria-hidden="true" />
        <span class="wc-atc-button-modern__multi-ot-label-text">{{ inOtherCartLabel }}</span>
      </span>
      <button
        type="button"
        class="wc-atc-button-modern__multi-ot-caret"
        :aria-label="t('chooseCart')"
        @click.stop="openPicker()"
      >
        <font-awesome-icon :icon="['fas', 'ellipsis-vertical']" aria-hidden="true" />
      </button>
    </div>

    <div
      v-else-if="(hasMultiOTPicker && cardState === 'plainBase') || requiresOtherOTConfirmation"
      class="wc-atc-button-modern__action"
      @click.capture="onPlainAddClick"
    >
      <slot></slot>
    </div>

    <div v-else-if="hasMultiOTPicker" class="wc-atc-button-modern__multi-ot">
      <div class="wc-atc-button-modern__multi-ot-add">
        <slot></slot>
      </div>
      <button
        type="button"
        class="wc-atc-button-modern__multi-ot-caret"
        :aria-label="t('chooseCart')"
        @click.stop="openPicker()"
      >
        <font-awesome-icon :icon="['fas', 'ellipsis-vertical']" aria-hidden="true" />
      </button>
    </div>

    <div v-else class="wc-atc-button-modern__action">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps, toRef, withDefaults } from 'vue';
import { useI18n } from 'vue-i18n';
import { BTN_VARIANT as ATLB_BTN_VARIANT } from '@/constants/AppConstants';
import useCartControls from '@/composables/useCartControls';
import useOrderTypes from '@/composables/useOrderTypes';
import useMultiOTPicker from '@/modules/orderTypes/composables/useMultiOTPicker';
import WCAddToListModernCardButton from '@/modules/lists/components/WCAddToListModernCardButton/WCAddToListModernCardButton.vue';
import ItemSummary from '@/models/entities/items/item-summary';

const props = withDefaults(
  defineProps<{
    item: ItemSummary;
    linkButtonType?: boolean;
    addToListVariant?: keyof typeof ATLB_BTN_VARIANT;
  }>(),
  {
    linkButtonType: false,
    addToListVariant: ATLB_BTN_VARIANT.ATC as keyof typeof ATLB_BTN_VARIANT,
  },
);

const { t } = useI18n();

const { isInStock, isInStoreOnly, isNotAvailable } = useCartControls();
const { isItemListOnly, isAcoMode } = useOrderTypes();
const {
  hasMultiOTPicker,
  requiresOtherOTConfirmation,
  cardState,
  otherOrderTypesWithQuantity,
  openPicker,
} = useMultiOTPicker(toRef(props, 'item'));

const notAvailable = computed(() => isNotAvailable(props.item));

const outOfStock = computed(
  () => !notAvailable.value && !isInStock(props.item) && !isInStoreOnly(props.item),
);

const inStoreOnly = computed(() => !notAvailable.value && isInStoreOnly(props.item));

const isMto = computed(() => Boolean(props.item.hasModifiers || props.item.hasLinkedItems));

const onPlainAddClick = (event: Event): void => {
  event.stopPropagation();
  event.preventDefault();
  openPicker();
};

const inOtherCartLabel = computed(() => {
  if (cardState.value === 'inOtherCart') {
    const orderTypeName = otherOrderTypesWithQuantity.value[0]?.orderType?.name ?? '';
    return t('inOrderTypeCart', { orderTypeName });
  }
  return t('inOtherOrderTypeCarts');
});

const listOnly = computed(() => isItemListOnly(props.item) && !isAcoMode.value);

const addToListInferredVariant = computed(() => {
  if (props.linkButtonType) {
    return ATLB_BTN_VARIANT.LINK;
  }

  return props.addToListVariant;
});
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.wc-atc-button-modern {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  width: 100%;
}

.wc-atc-button-modern__list {
  flex: 0 0 auto;
}

.wc-atc-button-modern__action {
  flex: 1 1 auto;
  min-width: 0;
}

.wc-atc-button-modern__multi-ot {
  display: flex;
  align-items: stretch;
  width: 100%;
  height: 2.5625rem;
  overflow: hidden;
  border-radius: 0 0 0.5rem 0.5rem;
}

.wc-atc-button-modern__multi-ot-add {
  flex: 1 1 auto;
  min-width: 0;
}

.wc-atc-button-modern__multi-ot-add :deep(.wc-atc-qty-adjuster-modern-card) {
  border-radius: 0 0 0 0.5rem;
}

.wc-atc-button-modern__multi-ot--label {
  background: var(--primary-lighter-9);
}

.wc-atc-button-modern__multi-ot-label {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--primary, $primary);
}

.wc-atc-button-modern__multi-ot-label-text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.wc-atc-button-modern__multi-ot-caret {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  border: none;
  border-top: 0.0625rem solid var(--dark-lighter-9);
  border-left: 0.0625rem solid var(--dark-lighter-9);
  background: var(--white);
  color: var(--primary, $primary);
  font-size: 0.75rem;
  cursor: pointer;
}

.wc-atc-button-modern__multi-ot:has(.wc-atc-qty-adjuster-modern-card)
  .wc-atc-button-modern__multi-ot-caret {
  border-right: 0.5px solid var(--dark-lighter-5);
  border-bottom: 0.5px solid var(--dark-lighter-5);
  border-radius: 0 0 0.5rem 0;
}

@media (hover: hover) {
  .wc-atc-button-modern__multi-ot-caret:hover {
    background: var(--primary-lighter-9);
  }
}

.wc-atc-button-modern__pill {
  display: flex;
  width: 100%;
  height: 2.625rem;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  gap: 0.31rem;
  background-color: var(--dark-lighter-4);
  opacity: 0.4;
  border: none;
  border-top: 0.03125rem solid var(--dark-lighter-5);
  color: var(--white);
  cursor: not-allowed;
}
</style>
